import { FC, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { Box } from '@mui/material';
import { clsx, Showable, useStyles } from '@sweatpals/react-ui-kit';
import { EventInstanceSortEnum, InterestsEnum } from '@sweatpals/shared';
import { eventsPageStyles } from './events-page.styles';
import { HeaderV2 } from '@/modules/_core/components/header/header-v2.component';
import HeroScreen from '../../components/hero-screen/hero-screen.component';
import HeroScreenSearchBar from '../../components/hero-screen-search-bar/hero-screen-search-bar.component';
import FooterV2 from '@/modules/_core/components/footer/footer-v2.component';
import GetAppSection from '../../components/get-app-section/get-app-section.component';
import { RangeEnum } from '@/modules/_core/enums/range';
import BrowseActivitiesByPeriods from '../../components/browse-activities-by-periods/browse-activities-by-periods.component';
import BrowseActivitiesByInterests from '../../components/browse-activities-by-interests/browse-activities-by-interests.component';
import CardSlider from '../../components/card-slider/card-slider.component';
import { eventsService } from '@/modules/_core/services';
import JoinCommunitiesSection from '../../components/join-communities-section/join-communities-section.component';
import EventCard from '../../components/event-card/event-card.component';
import { EventType } from '@/modules/_core/types/event-types';
import GetNewsLetterSectionV2 from '../../../landing/components/get-newsletter/get-newsletter-section-v2.component';
import useLink from '@/modules/_core/hooks/useLink';
import { INTERESTS, getCityIdForFilter, InterestsCategoryKeysEnum, IS_LEADERBOARD_ENABLED } from '@/modules/_core/constants';
import { useLocation } from '@/modules/_core/contexts/locationContext';
import { SentryFeedbackButton } from '@/modules/_core/components/buttons/SentryFeedbackButton';
import CreateOwnEventSection from '../../components/create-own-event-section/create-own-event-section.component';
import { InviteBanner } from '../../components/leaderboard/invite-banner';

const EventsPage: FC = observer(() => {
	const { locationCityId, locationPath } = useLocation();
	const router = useRouter();
	const { makeSearchEventsLink } = useLink();

	const styles = useStyles(eventsPageStyles);

	const [selectedPeriod, setSelectedPeriod] = useState<RangeEnum>(RangeEnum.ThisWeek);
	const [selectedInterests, setSelectedInterests] = useState<InterestsEnum[]>([]);

	const handlePeriodChange = (period: RangeEnum) => {
		setSelectedPeriod(period);
	};

	const handleInterestsChange = (interest: InterestsEnum) => {
		if (selectedInterests.includes(interest)) {
			setSelectedInterests(selectedInterests.filter(int => int !== interest));

			return;
		}

		setSelectedInterests([...selectedInterests, interest]);
	};

	const { data: eventCategories, isLoading: isCategoriesLoading } = useQuery(
		['event-categories', locationCityId, selectedPeriod, selectedInterests?.join('-')],
		async () =>
			eventsService.getNewHomeCategories({
				range: selectedPeriod,
				cityId: getCityIdForFilter(locationCityId),
				fitnessTypes: selectedInterests?.map(interest => ({
					id: interest,
				})),
			})
	);

	const editorPicks = useMemo(() => eventCategories?.[0], [eventCategories]);
	const justAdded = useMemo(() => eventCategories?.[1], [eventCategories]);

	const onSeeMoreClick = (categoryId: string) => {
		let link = '';

		if (categoryId === 'editor-picks') {
			link = makeSearchEventsLink({
				place: locationPath,
				range: selectedPeriod,
				isEditorPick: true,
				fitnessTypes: selectedInterests?.map(interest => ({
					id: interest,
				})),
			});
		} else if (categoryId === 'just-added') {
			link = makeSearchEventsLink({
				place: locationPath,
				range: selectedPeriod,
				sort: EventInstanceSortEnum.CreatedAtDesc,
				fitnessTypes: selectedInterests?.map(interest => ({
					id: interest,
				})),
			});
		} else if (categoryId === 'run-walk') {
			link = makeSearchEventsLink({
				place: locationPath,
				range: selectedPeriod,
				fitnessTypes: INTERESTS[InterestsCategoryKeysEnum.RunWalk].value,
			});
		} else if (categoryId === 'dance') {
			link = makeSearchEventsLink({
				place: locationPath,
				range: selectedPeriod,
				fitnessTypes: INTERESTS[InterestsCategoryKeysEnum.Dance].value,
			});
		} else if (categoryId === 'mindfullness') {
			link = makeSearchEventsLink({
				place: locationPath,
				range: selectedPeriod,
				fitnessTypes: INTERESTS[InterestsCategoryKeysEnum.Mindfulness].value,
			});
		} else if (categoryId === 'online') {
			link = makeSearchEventsLink({
				place: locationPath,
				range: selectedPeriod,
				isOnline: true,
				fitnessTypes: selectedInterests?.map(interest => ({
					id: interest,
				})),
			});
		}

		if (!link) {
			return;
		}

		router.push(link);
	};

	const cardSliderComponent = (event: EventType, isLoading: boolean) => (
		<EventCard eventData={event} showPriceBadge useEventLink showSkeleton={isLoading} />
	);

	return (
		<Box sx={styles.root}>
			<HeaderV2 />

			<Box>
				<HeroScreen
					backgroundSx={{
						background: 'grey',
					}}
					foregroundSx={{
						gap: '40px',
						textAlign: { xs: 'center' },
					}}
					background={
						<video id="header-video" preload="auto" autoPlay playsInline muted loop style={styles.videoStyles}>
							<source src={'/assets/video/background.webm'} type="video/webm" />
						</video>
					}
				>
					<HeroScreenSearchBar />
				</HeroScreen>

				<Showable show={IS_LEADERBOARD_ENABLED}>
					<InviteBanner />
				</Showable>

				<BrowseActivitiesByPeriods selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} />

				<Showable show={editorPicks?.data?.data?.length}>
					<CardSlider
						list={editorPicks?.data?.data ?? []}
						title={editorPicks?.renderName}
						component={cardSliderComponent}
						onSeeMoreClick={() => {
							onSeeMoreClick(editorPicks.id);
						}}
						isLoading={isCategoriesLoading}
						showSkeletonOnLoading
					/>
				</Showable>

				<BrowseActivitiesByInterests selectedInterests={selectedInterests} onInterestsChange={handleInterestsChange} />

				<Showable show={justAdded?.data?.data?.length}>
					<CardSlider
						list={justAdded?.data?.data ?? []}
						title={justAdded?.renderName}
						component={cardSliderComponent}
						onSeeMoreClick={() => {
							onSeeMoreClick(justAdded.id);
						}}
						isLoading={isCategoriesLoading}
						showSkeletonOnLoading
					/>
				</Showable>

				<CreateOwnEventSection></CreateOwnEventSection>
			</Box>

			<JoinCommunitiesSection cityId={locationCityId} />

			<GetAppSection
				sx={{
					root: styles.getAppSection,
				}}
			/>

			<Box sx={clsx(styles.flexColumn, styles.categoriesSection)}>
				{eventCategories
					?.slice(2)
					.filter(category => category?.data?.data?.length)
					.map(category => (
						<CardSlider
							key={category?.id}
							list={category.data.data}
							title={category.renderName}
							component={cardSliderComponent}
							onSeeMoreClick={() => {
								onSeeMoreClick(category.id);
							}}
							isLoading={isCategoriesLoading}
							showSkeletonOnLoading
						/>
					))}
			</Box>

			<GetNewsLetterSectionV2 sx={{ root: styles.getNewsSection }} />

			<FooterV2 />

			{/* <SentryFeedbackButton /> */}
		</Box>
	);
});

export default EventsPage;
